import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle2, Loader2, Minus, Plus, Package, Truck, ShieldCheck, MapPin, Tag, X as XIcon } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { api } from '../lib/api';
import { useSettings } from '../context/SettingsContext';

const inputCls =
  'bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-[#00b4d8] focus-visible:ring-offset-0 rounded-xl h-11';

// Fallback shipping fee if settings hasn't loaded yet — replaced live by settings.shop_shipping_fee_aed
const DEFAULT_SHIPPING_FEE_AED = 50;

const UAE_EMIRATES = [
  'Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman',
  'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah',
];

const parsePriceAED = (s) => {
  if (!s) return 0;
  const m = String(s).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : 0;
};

export const ShopCheckoutModal = ({ open, onOpenChange, product }) => {
  const { settings } = useSettings();
  const [qty, setQty] = useState(1);
  const [step, setStep] = useState('form'); // 'form' | 'pay' | 'paid'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null); // { order_ref, total_aed, ... }
  const [quote, setQuote] = useState(null); // paypal quote (currency+amount)

  // Promo code state — live-validated against POST /api/promo/validate
  const [promoInput, setPromoInput] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [promo, setPromo] = useState(null); // { code, discount_aed, message } — successful application
  const [promoError, setPromoError] = useState('');

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    area: '',
    city: '',
    emirate: 'Dubai',
    postal_code: '',
    notes: '',
  });

  // Shipping fee — comes from settings so the owner can change it in admin
  const shippingFee = useMemo(() => {
    const val = settings?.shop_shipping_fee_aed;
    if (val === undefined || val === null || val === '') return DEFAULT_SHIPPING_FEE_AED;
    const n = Number(val);
    return Number.isFinite(n) && n >= 0 ? n : DEFAULT_SHIPPING_FEE_AED;
  }, [settings?.shop_shipping_fee_aed]);

  const unit = useMemo(() => parsePriceAED(product?.price), [product]);
  const compareUnit = useMemo(() => parsePriceAED(product?.compare_at_price), [product]);
  const subtotal = useMemo(() => +(unit * qty).toFixed(2), [unit, qty]);
  const savings = useMemo(() => {
    if (!compareUnit || compareUnit <= unit) return 0;
    return +((compareUnit - unit) * qty).toFixed(2);
  }, [unit, compareUnit, qty]);
  const promoDiscount = promo?.discount_aed || 0;
  const grandTotal = useMemo(
    () => +(Math.max(subtotal - promoDiscount, 0) + shippingFee).toFixed(2),
    [subtotal, promoDiscount, shippingFee]
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Re-validate applied promo when subtotal changes (qty +/-)
  useEffect(() => {
    if (!promo?.code) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post('/promo/validate', { code: promo.code, subtotal_aed: subtotal, scope: 'shop' });
        if (!cancelled) {
          if (data.valid) setPromo({ code: data.code, discount_aed: data.discount_aed, message: data.message });
          else { setPromo(null); setPromoError(data.message || 'Promo no longer applies.'); }
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
     
  }, [subtotal]);

  const applyPromo = async () => {
    const code = (promoInput || '').trim();
    if (!code) return;
    setPromoBusy(true);
    setPromoError('');
    try {
      const { data } = await api.post('/promo/validate', { code, subtotal_aed: subtotal, scope: 'shop' });
      if (data.valid) {
        setPromo({ code: data.code, discount_aed: data.discount_aed, message: data.message });
        setPromoInput('');
      } else {
        setPromo(null);
        setPromoError(data.message || 'Invalid promo code.');
      }
    } catch (e) {
      setPromoError(e?.response?.data?.detail || 'Could not validate code.');
    } finally {
      setPromoBusy(false);
    }
  };

  const clearPromo = () => { setPromo(null); setPromoError(''); setPromoInput(''); };

  const reset = () => {
    setQty(1);
    setStep('form');
    setSubmitting(false);
    setError('');
    setOrder(null);
    setQuote(null);
    setPromo(null);
    setPromoInput('');
    setPromoError('');
    setForm({
      full_name: '', email: '', phone: '',
      address_line1: '', address_line2: '', area: '',
      city: '', emirate: 'Dubai', postal_code: '', notes: '',
    });
  };

  const handleClose = (next) => {
    if (!next) setTimeout(reset, 250);
    onOpenChange(next);
  };

  const validate = () => {
    if (!product) return 'No product selected.';
    if (!form.full_name.trim() || form.full_name.trim().length < 2) return 'Please enter your full name.';
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) return 'Please enter a valid email address.';
    if (!form.phone.trim() || form.phone.trim().length < 6) return 'Please enter a valid phone number.';
    if (!form.address_line1.trim()) return 'Please enter your street address.';
    if (!form.city.trim()) return 'Please enter your city.';
    if (!UAE_EMIRATES.includes(form.emirate)) return 'Please select an emirate.';
    if (!unit) return 'This product has no price configured. Please contact us.';
    return '';
  };

  const submitShipping = async (e) => {
    e?.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setSubmitting(true);
    try {
      const { data } = await api.post('/shop/orders', {
        items: [{
          product_id: product.id,
          product_slug: product.slug || '',
          product_name: product.name,
          unit_price_aed: unit,
          quantity: qty,
          image_url: product.image_url || '',
        }],
        shipping: {
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          address_line1: form.address_line1.trim(),
          address_line2: form.address_line2.trim(),
          area: form.area.trim(),
          city: form.city.trim(),
          emirate: form.emirate,
          postal_code: form.postal_code.trim(),
          country: 'United Arab Emirates',
          notes: form.notes.trim(),
        },
        shipping_fee_aed: shippingFee,
        promo_code: promo?.code || '',
      });
      setOrder(data);

      // fetch PayPal quote (currency + amount)
      try {
        const { data: q } = await api.get(`/payments/paypal/shop/quote/${data.order_ref}`);
        setQuote(q);
      } catch { /* PayPal disabled — user will get contact prompt */ }

      setStep('pay');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not place order.';
      setError(typeof msg === 'string' ? msg : 'Could not place order.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayPalError = (err) => {
    console.error('PayPal error', err);
    setError('Payment could not be completed. Please try again or contact us via WhatsApp.');
  };

  // ------- Success screen -------
  if (open && step === 'paid') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent data-testid="shop-checkout-modal" className="bg-[#07101f] border-white/12 text-white sm:max-w-md">
          <div className="text-center py-4">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/15 text-emerald-400 inline-flex items-center justify-center mb-4">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <div className="font-display text-2xl font-semibold">Payment received</div>
            <p className="mt-2 text-sm text-white/70">
              Thank you! Your order <span className="font-mono text-[#c8e600]" data-testid="shop-order-ref">{order?.order_ref}</span> is confirmed.
              We’ve emailed a receipt to <strong>{form.email}</strong> and our team will contact you shortly to schedule delivery to your UAE address.
            </p>
            <Button
              data-testid="shop-checkout-done"
              onClick={() => handleClose(false)}
              className="mt-6 bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100] font-semibold rounded-xl"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const paypalEnabled = !!settings?.paypal_enabled && !!settings?.paypal_public_client_id;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="shop-checkout-modal" className="bg-[#07101f] border-white/12 text-white sm:max-w-2xl max-h-[92vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-semibold flex items-center gap-2">
            <Package className="h-5 w-5 text-[#00b4d8]" /> Checkout
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Ship to any address in the UAE. Pay securely with PayPal.
          </DialogDescription>
        </DialogHeader>

        {/* Product summary */}
        {product && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 flex items-center gap-3">
            <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#0d223d] shrink-0">
              {product.image_url ? (
                <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <Package className="h-6 w-6 m-auto mt-5 text-white/40" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold leading-snug truncate">{product.name}</div>
              <div className="text-xs text-white/55 mt-0.5 flex items-baseline gap-2">
                <span>AED {unit.toFixed(2)} each</span>
                {compareUnit && compareUnit > unit && (
                  <>
                    <span className="line-through text-white/35 decoration-white/30">AED {compareUnit.toFixed(2)}</span>
                    <span className="inline-flex items-center rounded-full bg-red-500/20 text-red-300 border border-red-400/30 font-bold text-[9px] px-1.5 py-0.5">
                      −{Math.round(((compareUnit - unit) / compareUnit) * 100)}%
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button type="button" size="icon" variant="outline" data-testid="shop-qty-dec"
                onClick={() => setQty((n) => Math.max(1, n - 1))}
                disabled={step !== 'form'}
                className="h-8 w-8 border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-lg"
              ><Minus className="h-3.5 w-3.5" /></Button>
              <div data-testid="shop-qty-value" className="w-8 text-center font-semibold">{qty}</div>
              <Button type="button" size="icon" variant="outline" data-testid="shop-qty-inc"
                onClick={() => setQty((n) => Math.min(99, n + 1))}
                disabled={step !== 'form'}
                className="h-8 w-8 border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-lg"
              ><Plus className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="font-display text-lg font-semibold text-[#c8e600] shrink-0 min-w-[80px] text-right">AED {subtotal.toFixed(2)}</div>
          </div>
        )}

        {step === 'form' && (
          <form onSubmit={submitShipping} className="space-y-4 mt-2">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#00b4d8] font-semibold flex items-center gap-1.5">
              <MapPin className="h-3 w-3" /> Shipping details (UAE)
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80">Full name *</Label>
                <Input data-testid="shop-input-name" className={inputCls + ' mt-1.5'} value={form.full_name}
                  onChange={(e) => set('full_name', e.target.value)} placeholder="Jane Diver" />
              </div>
              <div>
                <Label className="text-white/80">Email *</Label>
                <Input data-testid="shop-input-email" type="email" className={inputCls + ' mt-1.5'} value={form.email}
                  onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" />
              </div>
              <div>
                <Label className="text-white/80">Phone *</Label>
                <Input data-testid="shop-input-phone" className={inputCls + ' mt-1.5'} value={form.phone}
                  onChange={(e) => set('phone', e.target.value)} placeholder="+971 5X XXX XXXX" />
              </div>
              <div>
                <Label className="text-white/80">Emirate *</Label>
                <Select value={form.emirate} onValueChange={(v) => set('emirate', v)}>
                  <SelectTrigger data-testid="shop-input-emirate" className={inputCls + ' mt-1.5 w-full'}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#07101f] border-white/15 text-white">
                    {UAE_EMIRATES.map((em) => (
                      <SelectItem key={em} value={em} className="focus:bg-white/10 focus:text-white">{em}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-white/80">Address line 1 *</Label>
                <Input data-testid="shop-input-addr1" className={inputCls + ' mt-1.5'} value={form.address_line1}
                  onChange={(e) => set('address_line1', e.target.value)} placeholder="Building name, street name" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-white/80">Address line 2 <span className="text-white/40 text-xs">(optional)</span></Label>
                <Input data-testid="shop-input-addr2" className={inputCls + ' mt-1.5'} value={form.address_line2}
                  onChange={(e) => set('address_line2', e.target.value)} placeholder="Apartment / Villa / Floor" />
              </div>
              <div>
                <Label className="text-white/80">Area / District <span className="text-white/40 text-xs">(optional)</span></Label>
                <Input data-testid="shop-input-area" className={inputCls + ' mt-1.5'} value={form.area}
                  onChange={(e) => set('area', e.target.value)} placeholder="e.g. JLT, Dubai Marina" />
              </div>
              <div>
                <Label className="text-white/80">City *</Label>
                <Input data-testid="shop-input-city" className={inputCls + ' mt-1.5'} value={form.city}
                  onChange={(e) => set('city', e.target.value)} placeholder="e.g. Dubai" />
              </div>
              <div>
                <Label className="text-white/80">Postal code <span className="text-white/40 text-xs">(optional)</span></Label>
                <Input data-testid="shop-input-postal" className={inputCls + ' mt-1.5'} value={form.postal_code}
                  onChange={(e) => set('postal_code', e.target.value)} placeholder="e.g. 00000" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-white/80">Delivery notes <span className="text-white/40 text-xs">(optional)</span></Label>
                <Textarea data-testid="shop-input-notes" rows={2} className={inputCls + ' mt-1.5 h-auto py-2'} value={form.notes}
                  onChange={(e) => set('notes', e.target.value)} placeholder="Landmark, best time to reach you, etc." />
              </div>
            </div>

            {error && (
              <div data-testid="shop-checkout-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {error}
              </div>
            )}

            {/* Promo code */}
            <div className="pt-1">
              {promo ? (
                <div data-testid="shop-promo-applied" className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.08] px-3 py-2.5 flex items-center gap-2.5">
                  <Tag className="h-4 w-4 text-emerald-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-emerald-200">
                      Code <span className="font-mono">{promo.code}</span> applied
                    </div>
                    <div className="text-[11px] text-emerald-200/80">−AED {promoDiscount.toFixed(2)} off your subtotal</div>
                  </div>
                  <button type="button" onClick={clearPromo} data-testid="shop-promo-remove" aria-label="Remove promo code" className="text-emerald-200/70 hover:text-emerald-100">
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div>
                  <Label className="text-white/80">Promo code <span className="text-white/40 text-xs">(optional)</span></Label>
                  <div className="mt-1.5 flex gap-2">
                    <Input
                      data-testid="shop-promo-input"
                      className={inputCls + ' font-mono uppercase tracking-wider flex-1'}
                      placeholder="WELCOME10"
                      value={promoInput}
                      onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); if (promoError) setPromoError(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPromo(); } }}
                    />
                    <Button
                      type="button"
                      data-testid="shop-promo-apply"
                      disabled={promoBusy || !promoInput.trim()}
                      onClick={applyPromo}
                      className="bg-white/10 hover:bg-white/15 text-white border border-white/15 font-semibold rounded-xl h-11 px-4"
                    >
                      {promoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                  {promoError && (
                    <div data-testid="shop-promo-error" className="mt-1.5 text-[11px] text-red-300">{promoError}</div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <div className="flex flex-col gap-0.5 text-sm text-white/70">
                <div className="inline-flex items-center gap-1.5">
                  <Truck className="h-4 w-4 text-[#00b4d8]" /> UAE delivery <span className="font-semibold text-white">{shippingFee > 0 ? `AED ${shippingFee}` : 'Free'}</span>
                </div>
                {promo && (
                  <div className="inline-flex items-center gap-1.5 text-emerald-300">
                    <Tag className="h-4 w-4" /> Promo <span className="font-mono">{promo.code}</span> −AED {promoDiscount.toFixed(2)}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-xs text-white/50">Order total (incl. shipping)</div>
                <div className="font-display text-2xl font-semibold text-[#c8e600]" data-testid="shop-checkout-form-total">AED {grandTotal.toFixed(2)}</div>
                {(savings > 0 || promoDiscount > 0) && (
                  <div className="text-[11px] text-emerald-300 mt-0.5" data-testid="shop-checkout-savings">
                    You save AED {(savings + promoDiscount).toFixed(2)}
                  </div>
                )}
              </div>
            </div>

            <Button
              type="submit"
              data-testid="shop-checkout-continue"
              disabled={submitting}
              className="w-full bg-[#0078d4] hover:bg-[#0066b8] text-white font-semibold rounded-xl h-12 text-base"
            >
              {submitting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Placing order…</> : <>Continue to Payment</>}
            </Button>
          </form>
        )}

        {step === 'pay' && order && (
          <div className="space-y-4 mt-2">
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-3 text-sm text-emerald-200 flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                Order <span className="font-mono">{order.order_ref}</span> created for <strong>{form.full_name}</strong>.
                Shipping to <strong>{form.emirate}, UAE</strong>. Complete secure PayPal payment below.
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#00b4d8] font-semibold mb-2">Order summary</div>
              <div className="flex items-center justify-between text-sm text-white/75"><span>Subtotal</span><span>AED {order.subtotal_aed?.toFixed?.(2) ?? order.subtotal_aed}</span></div>
              {(order.discount_aed > 0 || order.promo_code) && (
                <div className="flex items-center justify-between text-sm text-emerald-300">
                  <span className="inline-flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> Promo {order.promo_code}</span>
                  <span>−AED {order.discount_aed?.toFixed?.(2) ?? order.discount_aed}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm text-white/75">
                <span className="inline-flex items-center gap-1"><Truck className="h-3.5 w-3.5 text-[#00b4d8]" /> UAE delivery</span>
                <span>{Number(order.shipping_fee_aed) > 0 ? `AED ${order.shipping_fee_aed?.toFixed?.(2) ?? order.shipping_fee_aed}` : 'Free'}</span>
              </div>
              <div className="mt-2 pt-2 border-t border-white/10 flex items-center justify-between font-semibold">
                <span>Total</span>
                <span className="text-[#c8e600] text-lg" data-testid="shop-order-total">AED {order.total_aed?.toFixed?.(2) ?? order.total_aed}</span>
              </div>
              {quote?.amount && quote?.currency && quote.currency !== 'AED' && (
                <div className="mt-1 text-[11px] text-white/50 text-right">You will be charged {quote.amount} {quote.currency} via PayPal</div>
              )}
            </div>

            {paypalEnabled ? (
              <div data-testid="shop-paypal-buttons-wrap">
                <PayPalScriptProvider
                  options={{
                    'client-id': settings.paypal_public_client_id,
                    currency: quote?.currency || 'USD',
                    intent: 'capture',
                    components: 'buttons',
                  }}
                >
                  <PayPalButtons
                    style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' }}
                    createOrder={async () => {
                      const { data } = await api.post('/payments/paypal/shop/create-order', { order_ref: order.order_ref });
                      return data.id;
                    }}
                    onApprove={async (paypalData) => {
                      const { data: cap } = await api.post('/payments/paypal/shop/capture', {
                        order_ref: order.order_ref,
                        order_id: paypalData.orderID,
                      });
                      if (cap?.paid) setStep('paid');
                      else setError('Payment did not complete. Please try again.');
                    }}
                    onError={handlePayPalError}
                  />
                </PayPalScriptProvider>
              </div>
            ) : (
              <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/[0.06] p-4 text-sm text-yellow-100">
                Online payment is temporarily unavailable. Your order is saved — our team will contact you via <strong>{form.email}</strong> or <strong>{form.phone}</strong> to arrange payment on delivery.
              </div>
            )}

            <div className="text-[11px] text-white/45 inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" /> Secured by PayPal. Your card details never touch our servers.
            </div>

            {error && (
              <div data-testid="shop-checkout-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {error}
              </div>
            )}

            <Button
              variant="ghost"
              data-testid="shop-back-to-form"
              onClick={() => setStep('form')}
              className="w-full text-white/60 hover:text-white hover:bg-white/5"
            >
              &larr; Back to shipping details
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShopCheckoutModal;
